import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  getAudienceFields, 
  getAudienceOperators, 
  previewAudience, 
  createAudience as createAudienceAPI,
  updateAudience as updateAudienceAPI
} from '../api/audience';
import apiClient from '../api/client';
import useAuthToken from './useAuthToken';
import { getTenantCacheKey } from '../utils/auth';

export const useAudience = () => {
  const token = useAuthToken();
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch available fields for audience building
  const { data: rawFields = [], isLoading: isFieldsLoading } = useQuery({
    queryKey: [getTenantCacheKey('audience-fields')],
    queryFn: getAudienceFields
  });

  // Process fields to make sure they have consistent structure
  const fields = rawFields.map(field => {
    // Make sure field.name is in the format "table.column"
    const fieldParts = field.name.split('.');
    const recordType = field.recordType || fieldParts[0];
    
    return {
      ...field,
      // Extract recordType from field name if not already present
      recordType: recordType
    };
  });
  
  // Log all unique record types for debugging
  useEffect(() => {
    if (fields.length > 0) {
      const recordTypes = [...new Set(fields.map(f => f.recordType || f.name.split('.')[0]))];
      console.log('Available record types:', recordTypes);
      
      // Log fields by record type
      const fieldsByType = {};
      recordTypes.forEach(type => {
        fieldsByType[type] = fields.filter(f => (f.recordType || f.name.split('.')[0]) === type);
      });
      console.log('Fields grouped by record type:', fieldsByType);
    }
  }, [fields]);
  
  // Fetch available operators
  const { data: rawOperators = [], isLoading: isOperatorsLoading } = useQuery({
    queryKey: [getTenantCacheKey('audience-operators')],
    queryFn: getAudienceOperators
  });

  // Process operators to ensure consistent structure
  const operators = rawOperators.map(op => ({
    value: op.value,
    label: op.label || op.value.toLowerCase().replace(/_/g, ' ')
  }));
  
  // Preview audience function
  const handlePreviewAudience = useCallback(async (audienceData) => {
    try {
      // Just pass the correctly formatted data directly to the API function
      console.log('Sending preview request with data:', audienceData);
      const response = await previewAudience(audienceData);
      console.log('Preview response:', response);
      return response;
    } catch (error) {
      console.error('Error previewing audience:', error);
      throw error;
    }
  }, []);

  // Create audience function
  const createAudience = useCallback(async (audienceData) => {
    try {
      console.log('Creating audience with data:', audienceData);
      const response = await createAudienceAPI(audienceData);
      console.log('Create audience response:', response);
      return response;
    } catch (error) {
      console.error('Error creating audience:', error);
      throw error;
    }
  }, []);

  // Update existing audience function
  const updateAudience = useCallback(async (id, audienceData) => {
    try {
      setIsLoading(true);
      
      console.log(`Updating audience ${id} with data:`, JSON.stringify(audienceData, null, 2));
      
      // Using RESTful PATCH method with the new endpoint
      const res = await apiClient.patch(`/audience/${id}`, audienceData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Audience updated successfully:', res.data);
      return res.data;
    } catch (err) {
      console.error(`Error updating audience ${id}:`, err);
      
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      }
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Get audience by ID
  const getAudience = useCallback(async (id) => {
    try {
      setIsLoading(true);
      
      const res = await apiClient.get(`/audience/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return res.data;
    } catch (err) {
      console.error(`Error fetching audience ${id}:`, err);
      
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      }
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Validate audience
  const validateAudience = useCallback(async (filterData) => {
    try {
      setIsLoading(true);
      
      const res = await apiClient.post('/audience/validate', filterData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return res.data;
    } catch (err) {
      console.error('Error validating audience:', err);
      
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      }
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Preview complex filter
  const previewComplexFilter = useCallback(async (slug) => {
    try {
      setIsLoading(true);
      console.log(`Making request to preview complex filter: ${slug}`);
      // Use the correct API endpoint path
      const data = await apiClient.get(`/complex-filters/${slug}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data.data;
    } catch (error) {
      console.error('Error previewing complex filter:', error);
      console.error('Error details:', error.response?.data || error.message);
      return {
        matched_count: 0,
        sample_clients: []
      };
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Group fields by recordType for easier access
  const fieldsByRecordType = fields.reduce((acc, field) => {
    const recordType = field.recordType?.toLowerCase();
    if (recordType) {
      if (!acc[recordType]) {
        acc[recordType] = [];
      }
      acc[recordType].push(field);
    }
    return acc;
  }, {});

  return {
    fields,
    fieldsByRecordType,
    operators,
    isLoading: isFieldsLoading || isOperatorsLoading || isLoading,
    previewAudience: handlePreviewAudience,
    createAudience,
    updateAudience,
    getAudience,
    validateAudience,
    previewComplexFilter
  };
};